import { useState } from 'react';
import type { Member } from '../types';
import { PersonPicker } from './PersonPicker';

export interface FaceTagRowFace {
  cropUrl: string;
  assignedUserId: string | null;
}

interface FaceTagRowProps {
  faces: FaceTagRowFace[];
  /** Human members eligible to be assigned to a detected face. */
  members: Member[];
  onAssign: (faceIdx: number, userId: string | null) => void;
  /** Should throw on failure so the input/picker stays open for a retry. */
  onAddNewPerson: (faceIdx: number, name: string) => Promise<void>;
}

/**
 * One photo's row of detected-face crops, with an inline "who is this?"
 * picker. Shared by EventForm (per staged photo) and ClusterReviewCard (per
 * featured photo) — this UI is genuinely complex enough that duplicating it
 * would be a real DRY violation.
 */
export function FaceTagRow({ faces, members, onAssign, onAddNewPerson }: FaceTagRowProps) {
  const [selectingFaceIdx, setSelectingFaceIdx] = useState<number | null>(null);

  if (faces.length === 0) return null;

  return (
    <div>
      <p className="text-xs font-medium text-slate-500">
        {faces.length === 1 ? 'We found 1 face' : `We found ${faces.length} faces`} — tap to tag who it is{' '}
        <span className="font-normal text-slate-400">(optional)</span>
      </p>
      <p className="text-[11px] text-slate-400 mb-1.5">
        Tagging lets you find and play this by person later. You can skip it.
      </p>
      <div className="flex flex-wrap gap-2">
        {faces.map((face, fi) => {
          const assignedMember = face.assignedUserId ? members.find(m => m.user.id === face.assignedUserId) : null;
          const isSelecting = selectingFaceIdx === fi;
          return (
            <button
              key={fi}
              type="button"
              onClick={() => setSelectingFaceIdx(isSelecting ? null : fi)}
              className={`relative flex flex-col items-center gap-1 p-1 rounded-lg border-2 transition-all ${
                face.assignedUserId
                  ? 'border-indigo-500 bg-indigo-50'
                  : isSelecting
                    ? 'border-violet-400 bg-violet-50'
                    : 'border-dashed border-slate-300 hover:border-indigo-300'
              }`}
            >
              <img src={face.cropUrl} className="w-12 h-12 rounded object-cover" alt="detected face" />
              <span className="text-[10px] max-w-[52px] truncate text-center font-medium text-slate-700">
                {assignedMember ? (assignedMember.user.name ?? '?').split(' ')[0] : '?'}
              </span>
            </button>
          );
        })}
      </div>

      {selectingFaceIdx !== null && faces[selectingFaceIdx] && (() => {
        const fi = selectingFaceIdx;
        const face = faces[fi];
        return (
          <div className="mt-2">
            <PersonPicker
              members={members}
              headerCropUrl={face.cropUrl}
              assignedUserId={face.assignedUserId}
              onAssign={userId => { onAssign(fi, userId); setSelectingFaceIdx(null); }}
              onRemove={face.assignedUserId ? () => { onAssign(fi, null); setSelectingFaceIdx(null); } : undefined}
              onAddNewPerson={name => onAddNewPerson(fi, name)}
              onClose={() => setSelectingFaceIdx(null)}
            />
          </div>
        );
      })()}
    </div>
  );
}
